// SelectMenu — branded dropdown that replaces the native <select> when
// we want full control over both the trigger and the menu panel.
//
// Trigger: same 44px height as our Input, accent left-border, current
// value, chevron. Menu: brand-styled rows below the trigger, keyboard
// navigable, click-outside / Esc to close.
//
// Usage:
//   <SelectMenu
//     value={tab}
//     options={[{ value: "overview", label: "Overview" }, ...]}
//     onChange={setTab}
//   />

import { useEffect, useRef, useState } from "react";

export interface SelectOption<T extends string> {
  value: T;
  label: string;
}

interface SelectMenuProps<T extends string> {
  value: T;
  options: SelectOption<T>[];
  onChange: (value: T) => void;
  /** Optional accessible label for the trigger button. */
  ariaLabel?: string;
  /** Tailwind classes appended to the trigger button. */
  className?: string;
}

export function SelectMenu<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  className = "",
}: SelectMenuProps<T>) {
  const [open, setOpen] = useState(false);
  const [keyboardIdx, setKeyboardIdx] = useState<number>(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const current = options.find((o) => o.value === value);
  const currentLabel = current?.label ?? String(value);

  // Click outside closes.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  // Esc closes; arrow keys + Enter handle keyboard nav while open.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setKeyboardIdx((i) => Math.min(options.length - 1, i + 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setKeyboardIdx((i) => Math.max(0, i - 1));
      } else if (e.key === "Enter" && keyboardIdx >= 0) {
        e.preventDefault();
        const opt = options[keyboardIdx];
        if (opt) {
          onChange(opt.value);
          setOpen(false);
          triggerRef.current?.focus();
        }
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, keyboardIdx, options, onChange]);

  // When the menu opens, seed keyboard cursor to the current value so
  // the user can arrow up/down from a meaningful starting point.
  useEffect(() => {
    if (open) {
      const idx = options.findIndex((o) => o.value === value);
      setKeyboardIdx(idx >= 0 ? idx : 0);
    }
  }, [open, options, value]);

  // Focus the keyboard-active option as the user arrows through.
  useEffect(() => {
    if (open && keyboardIdx >= 0) {
      optionRefs.current[keyboardIdx]?.focus();
    }
  }, [open, keyboardIdx]);

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={
          "w-full flex items-center justify-between gap-2 " +
          "pl-4 pr-3 py-3 min-h-[44px] " +
          "bg-bg2 border border-stroke rounded text-content " +
          "font-mono text-base outline-none " +
          "hover:border-stroke-hv transition-colors cursor-pointer " +
          className
        }
        style={{ borderLeft: "3px solid rgb(var(--color-accent))" }}
      >
        <span className="truncate text-left">{currentLabel}</span>
        <span
          aria-hidden="true"
          className="text-accent text-[12px] transition-transform shrink-0"
          style={{ transform: open ? "rotate(180deg)" : "none" }}
        >
          ▼
        </span>
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label={ariaLabel}
          className="absolute left-0 right-0 z-50 mt-1 max-h-[60vh] overflow-y-auto rounded animate-fade-in"
          style={{
            background: "rgb(var(--color-bg2))",
            border: "1px solid rgb(var(--color-stroke))",
            boxShadow: "0 12px 32px rgba(0,0,0,0.6)",
          }}
        >
          {options.map((opt, i) => {
            const selected = opt.value === value;
            return (
              <li key={opt.value} role="option" aria-selected={selected}>
                <button
                  ref={(el) => {
                    optionRefs.current[i] = el;
                  }}
                  type="button"
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                    triggerRef.current?.focus();
                  }}
                  className={
                    "w-full text-left px-4 py-3 min-h-[44px] " +
                    "font-mono text-[14px] cursor-pointer " +
                    "border-l-[3px] transition-colors " +
                    (selected
                      ? "border-accent text-accent bg-accent-bg"
                      : "border-transparent text-content hover:bg-bg2 focus:bg-bg2 focus:outline-none")
                  }
                >
                  {opt.label}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
